import React from 'react';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, Link as RouterLink } from 'react-router-dom';
// Composants
import App from '../../components/app';
import ErrorMessage from '../../components/error-message';
import FormBoolean from '../../components/form/formBoolean';
import FormInput from '../../components/form/formInput';
import EnhancedListItem from '../../components/listItem';
import { ListCell } from '../../local-types';
// Modules
import { RiderReserver } from '../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../redux/reducers/errors';
import { actions as riderActions, selectors as riderSelectors } from '../../redux/reducers/riders';
import { actions as reservationActions } from '../../redux/reducers/reservations';
// Ressources
import withGlobalStyles from '../../utils/global-styles';
import { paths } from '../paths';
import FormSubmit from '../../components/form/formSubmit';
import FormUtils from '../../utils/forms';
import { clsx } from '../../utils/htmlUtils';

interface ReserverProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    foundRider: RiderReserver;
    foundRiders: RiderReserver[];
    findRiderError: ErrorPayload;
    findRiderWithPhoneError: ErrorPayload;
    findReservationError: ErrorPayload;
    findRider: (payload: { email?: string; phone?: string }) => void;
    setFoundRider: (rider: RiderReserver) => void;
    resetFoundRiders: () => void;
    findRidersWithPhone: (payload: { phone_number: string }) => void;
    findReservation: (number: string, callback: Function) => void;
}

const ReserverPage = (props: ReserverProps): JSX.Element => {
    const classes = useStyles();

    const {
        t,
        history,
        globalClasses,
        foundRider,
        foundRiders,
        findRiderError,
        findRiderWithPhoneError,
        findReservationError,
        findRider,
        setFoundRider,
        resetFoundRiders,
        findRidersWithPhone,
        findReservation,
    } = props;

    const formRef = React.useRef<HTMLFormElement>();
    const formSearchRef = React.useRef<HTMLFormElement>();
    const formPhoneRef = React.useRef<HTMLFormElement>();
    const [isActive, setActive] = React.useState<boolean>(false);
    const [isSearchActive, setSearchActive] = React.useState<boolean>(false);
    const [isPhoneActive, setPhoneActive] = React.useState<boolean>(false);
    const [emailValid, setEmailValid] = React.useState<boolean>(false);
    const [phoneValid, setPhoneValid] = React.useState<boolean>(false);
    const [phoneValidAlone, setPhoneValidAlone] = React.useState<boolean>(false);
    const [banned, setBanned] = React.useState<boolean>(false);
    const [isValid, setValid] = React.useState<boolean>(false);
    const [selectedRider, setSelectedRider] = React.useState<any>(null);
    const [errorNotFound, setErrorNotFound] = React.useState<ErrorPayload>(null);
    const [errorNotFoundPhone, setErrorNotFoundPhone] = React.useState<ErrorPayload>(null);
    const [errorNotFoundReserver, setErrorNotFoundReserver] = React.useState<ErrorPayload>(null);

    React.useEffect(() => {
        canValidate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid]);

    //TODO: work
    React.useEffect(() => {
        const fields = FormUtils.getFields(formRef.current);

        const hasPhone = !!fields.phone_number;
        const hasEmail = !!fields.email;

        if (hasPhone && emailValid && foundRider?.id) {
            setPhoneValid(true);
            setActive(false);
        }
        if (hasEmail && !emailValid && foundRider?.id) {
            setEmailValid(true);
            setActive(false);
        }

        if (!foundRider?.id) {
            if (!emailValid && hasEmail) {
                setErrorNotFound({ details: t('error.email', { email: fields.email }), errorType: '' });
            }
            if (!phoneValid && hasPhone) {
                setErrorNotFound({ details: t('error.phone', { phone: fields.phone_number }), errorType: '' });
            }
        }
        if (foundRider?.banned?.value) {
            const { banned } = foundRider;
            setBanned(true);
            setErrorNotFound({
                details:
                    t('error.banned', { email: fields.email }) +
                    (banned.description ? ` ${t('common:ban_reason', { reason: banned.description })}` : ''),
                errorType: '',
            });
        }
        if (foundRider && foundRider.isModeCB && !foundRider.cb) {
            setErrorNotFound({ details: t('error.cb'), errorType: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [foundRider]);

    //TODO: work
    React.useEffect(() => {
        const fields = FormUtils.getFields(formPhoneRef.current);

        const hasPhone = !!fields.phone_number;

        if (hasPhone && foundRiders.length > 0) {
            setPhoneValidAlone(true);
        }

        let rider = null;
        if (foundRiders.length === 1) {
            rider = foundRiders[0];
        }
        if (foundRiders.length > 1 && selectedRider) {
            rider = foundRiders[selectedRider];
        }

        if (rider?.banned?.value) {
            const { banned } = rider;
            setBanned(true);
            setErrorNotFoundPhone({
                details:
                    t('error.banned', { email: rider.email }) +
                    (banned.description ? ` ${t('common:ban_reason', { reason: banned.description })}` : ''),
                errorType: '',
            });
        } else {
            setBanned(false);
            setErrorNotFoundPhone(null);
        }

        if (!rider?.id && foundRiders.length < 1) {
            if (!phoneValidAlone && hasPhone) {
                setErrorNotFoundPhone({ details: t('error.phone', { phone: fields.phone_number }), errorType: '' });
            }
        }

        if (rider && rider.isModeCB && !rider.cb) {
            setErrorNotFoundPhone({ details: t('error.cb'), errorType: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [foundRiders, selectedRider]);

    const iconDone = (
        <Icon style={{ color: '#20c5b5' }} fontSize="small">
            check_circle
        </Icon>
    );

    //================================================//
    //=============== Rider with Email ===============//
    //================================================//

    const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        const fields = FormUtils.getFields(formRef.current);
        setErrorNotFound(null);
        findRider(fields);
    };

    const canValidate = (): void => {
        const fields = FormUtils.getFields(formRef.current);
        if (!emailValid && !!!fields.email) {
            setActive(false);
            return;
        }
        if (emailValid && !phoneValid && !!!fields.phone_number) {
            setActive(false);
            return;
        }
        if (emailValid && phoneValid && !isValid) {
            setActive(false);
            return;
        }
        if (banned) {
            setActive(false);
            return;
        }
        if (foundRider.isModeCB && !foundRider.cb) {
            setActive(false);
            return;
        }
        setActive(true);
    };

    const handleValid = (event, value): void => {
        setValid(value);
    };

    const generateListItem = (user: RiderReserver): ListCell[] => {
        const listItem: ListCell[] = [
            { title: t('common:form.firstname'), data: user.firstname },
            { title: t('common:form.lastname'), data: user.lastname },
        ];

        if (user?.id && user.isModeCB) {
            listItem.push({ title: t('validation.cb'), data: user.cb });
        }
        if (user?.company) {
            let company = user.company.name;
            if (user.company?.parent) {
                company = `${user.company.parent.name} (${company})`;
            }
            listItem.push({ title: t('common:form.company'), data: company });
        }

        return listItem;
    };

    //======================================//
    //=============== Search ===============//
    //======================================//

    const canSearchValidate = (): void => {
        const fields = FormUtils.getFields(formSearchRef.current);
        if (!!!fields.reservation) {
            setSearchActive(false);
            return;
        }
        setSearchActive(true);
    };

    const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        const fields = FormUtils.getFields(formSearchRef.current);
        setErrorNotFoundReserver(null);
        if (fields.reservation) {
            findReservation(fields.reservation, (response) => {
                if (response && response.id && response.type) {
                    history.push(`${response.type === 'booking' ? paths.bookings : paths.reservations}/${response.id}`);
                } else {
                    setErrorNotFoundReserver({
                        details: t('error.reservation', { number: fields.reservation }),
                        errorType: '',
                    });
                }
            });
        }
    };

    //==========================================//
    //=============== Phone Only ===============//
    //==========================================//

    const canPhoneValidate = (): void => {
        const fields = FormUtils.getFields(formPhoneRef.current);
        if (!!!fields.phone_number) {
            setPhoneActive(false);
            return;
        }
        setPhoneActive(true);
    };

    const onPhoneSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        e.stopPropagation();

        const fields = FormUtils.getFields(formPhoneRef.current);
        setErrorNotFoundPhone(null);
        if (fields.phone_number) {
            findRidersWithPhone({ phone_number: fields.phone_number });
        }
    };

    let rider = null;
    if (foundRiders.length === 1) {
        rider = foundRiders[0];
    }
    if (foundRiders.length > 1 && selectedRider) {
        rider = foundRiders[selectedRider];
    }

    return (
        <App
            noFav
            header={{
                title: t('description'),
            }}
        >
            <FormSubmit debounceSubmit={{ callback: onPhoneSubmit }} ref={formPhoneRef}>
                <div style={{ display: 'flex' }}>
                    <div className={clsx(globalClasses.block, classes.flex, classes.blockMargin)}>
                        <p className={globalClasses.title}>{t('validation.client')}</p>
                        <FormInput
                            id={'phone_number'}
                            name={'phone_number'}
                            type={'tel'}
                            label={t('common:form.phone_number')}
                            required={true}
                            auto={true}
                            value={null}
                            inputProps={{
                                readOnly: phoneValidAlone,
                            }}
                            endAdornment={phoneValidAlone && iconDone}
                            handleChange={canPhoneValidate}
                        />
                        {foundRiders.length > 1 && (
                            <FormControl key={'formExtras'} margin="dense">
                                <div className={classes.formLabel}>{t('common:form.email')}</div>
                                <Select
                                    native
                                    id={'riderSelect'}
                                    name={'riderSelect'}
                                    variant="outlined"
                                    required={true}
                                    onChange={(e) => {
                                        setSelectedRider(e.target.value);
                                    }}
                                >
                                    <option key={'empty'} value={''}>
                                        {''}
                                    </option>
                                    {foundRiders.map((select, i) => {
                                        return (
                                            <option key={select.id} value={i}>
                                                {select.email}
                                            </option>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        )}
                        {foundRiders.length === 1 && (
                            <FormInput
                                id={'email'}
                                name={'email'}
                                type={'mail'}
                                label={t('common:form.email')}
                                value={foundRiders[0]?.email}
                                inputProps={{
                                    readOnly: true,
                                }}
                            />
                        )}
                        <div className={classes.buttons}>
                            {isPhoneActive && foundRiders?.length > 0 ? (
                                <Button
                                    startIcon={<Icon>done</Icon>}
                                    variant="contained"
                                    type="submit"
                                    className={globalClasses.roundButton}
                                    style={{ marginRight: 0, marginTop: 30 }}
                                    onClick={(e) => {
                                        setPhoneValidAlone(false);
                                        setFoundRider(rider);
                                        resetFoundRiders();
                                    }}
                                    component={RouterLink}
                                    to={`${paths.reserver}/${rider?.id}`}
                                    disabled={!rider || banned || !!errorNotFoundPhone}
                                    color={!rider || banned || !!errorNotFoundPhone ? 'inherit' : 'secondary'}
                                >
                                    {t('validation.ok')}
                                </Button>
                            ) : (
                                <Button
                                    startIcon={<Icon>done</Icon>}
                                    variant="contained"
                                    type="submit"
                                    className={globalClasses.roundButton}
                                    style={{ marginRight: 0, marginTop: 30 }}
                                    disabled={!isPhoneActive}
                                    color={!isPhoneActive ? 'inherit' : 'secondary'}
                                >
                                    {t('common:validate')}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={clsx(globalClasses.block, classes.flex, classes.justifyCenter)}>
                        <ErrorMessage payload={errorNotFoundPhone} />
                        <ErrorMessage payload={findRiderWithPhoneError} />
                        {phoneValidAlone && rider && (
                            <EnhancedListItem
                                datas={generateListItem(rider)}
                                title={t('form.general_infos')}
                                nbColumn={1}
                                noCard={true}
                            />
                        )}
                        {(!phoneValidAlone || (phoneValidAlone && !rider)) && (
                            <p className={globalClasses.details} style={{ whiteSpace: 'pre-wrap' }}>
                                {foundRiders.length > 1 ? t('script.select_email') : t('script.phone')}
                            </p>
                        )}
                    </div>
                </div>
            </FormSubmit>

            <FormSubmit debounceSubmit={{ callback: onSubmit }} ref={formRef}>
                <div style={{ display: 'flex' }}>
                    <div className={clsx(globalClasses.block, classes.flex, classes.blockMargin)}>
                        <p className={globalClasses.title}>{t('validation.client')}</p>
                        <FormInput
                            id={'email'}
                            name={'email'}
                            type={'email'}
                            label={t('common:form.email')}
                            required={true}
                            auto={true}
                            value={null}
                            inputProps={{
                                readOnly: emailValid,
                            }}
                            endAdornment={emailValid && !banned && iconDone}
                            handleChange={canValidate}
                        />
                        {emailValid && !banned && (
                            <FormInput
                                id={'phone_number'}
                                name={'phone_number'}
                                type={'tel'}
                                label={t('common:form.phone_number')}
                                required={true}
                                auto={true}
                                value={null}
                                inputProps={{
                                    readOnly: phoneValid,
                                }}
                                endAdornment={phoneValid && iconDone}
                                handleChange={canValidate}
                            />
                        )}
                        {emailValid && phoneValid && !banned && (
                            <>
                                <p className={globalClasses.title}>{t('validation.check')}</p>
                                <FormBoolean
                                    id={'isOk'}
                                    name={'isOk'}
                                    label={t('validation.check_details')}
                                    value={isValid}
                                    reverse={true}
                                    handleChange={handleValid}
                                />
                            </>
                        )}
                        <div className={classes.buttons}>
                            {emailValid && phoneValid && foundRider?.id && !banned ? (
                                <Button
                                    startIcon={<Icon>done</Icon>}
                                    variant="contained"
                                    type="submit"
                                    className={globalClasses.roundButton}
                                    style={{ marginRight: 0, marginTop: 30 }}
                                    component={RouterLink}
                                    to={`${paths.reserver}/${foundRider.id}`}
                                    disabled={!isActive}
                                    color={!isActive ? 'inherit' : 'secondary'}
                                >
                                    {t('validation.ok')}
                                </Button>
                            ) : (
                                <Button
                                    startIcon={<Icon>done</Icon>}
                                    variant="contained"
                                    type="submit"
                                    className={globalClasses.roundButton}
                                    style={{ marginRight: 0, marginTop: 30 }}
                                    disabled={!isActive}
                                    color={!isActive ? 'inherit' : 'secondary'}
                                >
                                    {t('common:validate')}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={clsx(globalClasses.block, classes.flex, classes.justifyCenter)}>
                        <ErrorMessage payload={errorNotFound} />
                        <ErrorMessage payload={findRiderError} />
                        {emailValid && phoneValid && foundRider?.id && (
                            <EnhancedListItem
                                datas={generateListItem(foundRider)}
                                title={t('form.general_infos')}
                                nbColumn={1}
                                noCard={true}
                            />
                        )}
                        {(!emailValid || !phoneValid) && (
                            <p className={globalClasses.details} style={{ whiteSpace: 'pre-wrap' }}>
                                {t(!emailValid ? 'script.email' : 'script.phone')}
                            </p>
                        )}
                    </div>
                </div>
            </FormSubmit>

            <FormSubmit debounceSubmit={{ callback: onSearchSubmit }} ref={formSearchRef}>
                <div style={{ display: 'flex' }}>
                    <div className={clsx(globalClasses.block, classes.flex, classes.blockMargin)}>
                        <p className={globalClasses.title}>{t('reservation.find')}</p>
                        <FormInput
                            id={'reservation'}
                            name={'reservation'}
                            label={t('form.reservation')}
                            placeholder={t('form.reservation_placeholder')}
                            required={true}
                            auto={true}
                            value={null}
                            handleChange={canSearchValidate}
                        />
                        <div className={classes.buttons}>
                            <Button
                                startIcon={<Icon>done</Icon>}
                                variant="contained"
                                type="submit"
                                className={globalClasses.roundButton}
                                style={{ marginRight: 0, marginTop: 30 }}
                                disabled={!isSearchActive}
                                color={!isSearchActive ? 'inherit' : 'secondary'}
                            >
                                {t('common:validate')}
                            </Button>
                        </div>
                    </div>
                    <div className={clsx(globalClasses.block, classes.flex, classes.justifyCenter)}>
                        <ErrorMessage payload={errorNotFoundReserver} />
                        <ErrorMessage payload={findReservationError} />
                        <p className={globalClasses.details} style={{ whiteSpace: 'pre-wrap' }}>
                            {t('script.reservation')}
                        </p>
                    </div>
                </div>
            </FormSubmit>
        </App>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        blockMargin: { marginRight: 40 },
        flex: { flex: 1 },
        justifyCenter: { justifyContent: 'center' },
        buttons: { display: 'flex', justifyContent: 'flex-end' },
        formLabel: {
            fontSize: 12,
            color: theme.palette.primary.dark,
            fontWeight: 500,
        },
    }),
);

const ReserverPageWithTranslation = withGlobalStyles(withTranslation(['reserver', 'common'])(ReserverPage));

const mapStateToProps = (state) => ({
    foundRider: riderSelectors.foundRider(state),
    foundRiders: riderSelectors.foundRiders(state),
    findRiderError: errorSelectors.findRider(state),
    findRiderWithPhoneError: errorSelectors.findRiderWithPhone(state),
    findReservationError: errorSelectors.findReservation(state),
});

const mapDispatchToProps = {
    findRider: riderActions.findRider,
    setFoundRider: riderActions.setFoundRider,
    resetFoundRiders: riderActions.resetFoundRiders,
    findRidersWithPhone: riderActions.findRidersWithPhone,
    findReservation: reservationActions.findReservation,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReserverPageWithTranslation);
